import React, { useEffect, useMemo, useState } from "react";
import RampsService from "../../services/RampsService";
import RampsConfirmModal from "../../components/Ramps/RampsConfirmModal";
import RampsPrecheckModal from "../../components/Ramps/RampsPrecheckModal";
import { parseCsv } from "../../utils/csv";
import { normalizePixRow } from "../../utils/pix";

export default function Ramps() {
  // BYPASS DEV: permite testar os modais mesmo com saldo 0 e simular o envio (POST)
  const IS_DEV = process.env.NODE_ENV !== "production";

  const [balance, setBalance] = useState(null);
  const [depositAddress, setDepositAddress] = useState(null);

  const emptyRow = {
    pixKey: "",
    pixKeyType: "CPF",
    name: "",
    doc: "",
    amountBRL: "",
    message: "",
  };

  const [formData, setFormData] = useState([emptyRow]);
  const [showPrecheck, setShowPrecheck] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileName, setFileName] = useState("");

  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState("");

  const totalBrl = useMemo(
    () => formData.reduce((a, r) => a + (Number(r.amountBRL || 0) || 0), 0),
    [formData]
  );

  useEffect(() => {
    (async () => {
      try {
        const userEmail =
          localStorage.getItem("emailUser") ||
          localStorage.getItem("user_email") ||
          localStorage.getItem("email") ||
          "";
        if (!userEmail) {
          console.warn(
            "Ramps: e-mail não encontrado no localStorage (emailUser/user_email/email)."
          );
          return;
        }
        const [balanceData, addressData] = await Promise.all([
          RampsService.getBalance(userEmail),
          RampsService.getDepositAddress(userEmail),
        ]);
        setBalance(balanceData);
        setDepositAddress(addressData);
      } catch (err) {
        console.error("Erro ao obter dados iniciais:", err);
      }
    })();
  }, []);

  useEffect(() => {
    const email =
      localStorage.getItem("emailUser") ||
      localStorage.getItem("user_email") ||
      localStorage.getItem("email") ||
      "";
    if (!email) return;
    const load = async () => {
      try {
        const b = await RampsService.getBalance(email);
        setBalance(b);
      } catch (e) {
        console.error("Erro ao atualizar saldo (polling):", e);
      }
    };
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const requestQuote = async () => {
    try {
      setQuoteError("");
      setQuoteLoading(true);
      const data = await RampsService.createCriptoQuote({
        email:
          localStorage.getItem("emailUser") ||
          localStorage.getItem("user_email") ||
          localStorage.getItem("email") ||
          "",
        side: "buy",
        quantity: "1",
        baseCurrency: "usdt",
        quoteCurrency: "brl",
      });
      const baseAmount = Number(data?.base_amount ?? data?.baseAmount ?? 1);
      const quoteAmount = Number(data?.quote_amount ?? data?.quoteAmount ?? 0);
      const rawPrice = Number(data?.price ?? 0);
      let unitPrice =
        baseAmount > 0 && quoteAmount > 0 ? quoteAmount / baseAmount : 0;
      if (!unitPrice && rawPrice) unitPrice = rawPrice / (baseAmount || 1);
      if (unitPrice > 0 && unitPrice < 1) unitPrice = 1 / unitPrice;
      setQuote({
        unitPrice,
        quoteAmount,
        baseAmount,
        rawPrice,
        marketSymbol: String(data?.market_symbol || "USDT/BRL"),
        side: String(data?.side || "BUY"),
        raw: data,
      });
    } catch (err) {
      console.error("Falha ao obter cotação:", err);
      setQuoteError("Não foi possível obter a cotação no momento.");
    } finally {
      setQuoteLoading(false);
    }
  };

  useEffect(() => {
    requestQuote();
  }, []);

  const handleChange = (index, field, value) => {
    const updated = [...formData];
    updated[index][field] = value;
    setFormData(updated);
  };

  const addRow = () => setFormData([...formData, { ...emptyRow }]);

  const removeRow = (i) => {
    const next = formData.filter((_, idx) => idx !== i);
    setFormData(next.length ? next : [{ ...emptyRow }]);
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      const normalized = rows
        .map((r) =>
          typeof normalizePixRow === "function" ? normalizePixRow(r) : r
        )
        .map((r) => ({
          pixKey: r.pixKey ?? r.chave ?? r.key ?? "",
          pixKeyType: r.pixKeyType ?? r.tipo ?? "CPF",
          name: r.name ?? r.nome ?? "",
          doc: r.doc ?? r.documento ?? "",
          amountBRL: r.amountBRL ?? r.amount ?? r.valor ?? r.value ?? "",
          message: r.message ?? r.mensagem ?? "",
        }));
      setFormData(normalized.length ? normalized : [{ ...emptyRow }]);
      setFileName(file.name);
    } catch (err) {
      console.error("Erro ao ler CSV:", err);
      alert("Não foi possível ler o CSV. Verifique o formato.");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const allValid = formData.every((row) => {
      const pixOk = !!row.pixKey;
      const amtOk = Number(row.amountBRL) > 0;
      const nameOk = !!row.name;
      const docOk = !!row.doc;
      return pixOk && amtOk && nameOk && docOk;
    });
    if (!allValid) {
      alert(
        "Por favor, valide chave Pix, valor (BRL), nome e documento antes de continuar."
      );
      return;
    }
    setShowPrecheck(true);
  };

  const confirmSubmit = async () => {
    setIsSubmitting(true);
    try {
      const userEmail =
        localStorage.getItem("emailUser") ||
        localStorage.getItem("user_email") ||
        localStorage.getItem("email") ||
        "";
      const onlyDigits = (s = "") => (s || "").replace(/\D/g, "");
      const payload = {
        withdrawalList: formData.map((r) => ({
          amount: Number(r.amountBRL || 0),
          pixKey: String(r.pixKey || "").trim(),
          pixKeyType: String(r.pixKeyType || "CPF"),
          name: String(r.name || "").trim(),
          doc: onlyDigits(r.doc),
          message: String(r.message || "").trim(),
        })),
      };

      if (IS_DEV) {
        await new Promise((r) => setTimeout(r, 700));
        console.log("[DEV] Remessa simulada com sucesso:", payload);
        alert("Remessa (SIMULADA) enviada com sucesso!");
      } else {
        await RampsService.postWithdrawPix(userEmail, payload);
        alert("Remessa enviada com sucesso!");
      }
      setFormData([{ ...emptyRow }]);
      setFileName("");
    } catch (err) {
      console.error("Erro ao enviar remessa:", err);
      alert("Falha ao enviar remessa. Tente novamente.");
    } finally {
      setIsSubmitting(false);
      setShowModal(false);
    }
  };

  const walletUsdt = Number(balance?.usdtFree ?? 0);

  const amountsInvalid = !formData.every(
    (r) => r.pixKey && Number(r.amountBRL) > 0 && r.name && r.doc
  );
  const submitDisabled =
    isSubmitting || amountsInvalid || (!IS_DEV && walletUsdt <= 0);

  useEffect(() => {
    if (!IS_DEV) return;
    window.__rampsDebug = {
      openPrecheck: () => setShowPrecheck(true),
      openConfirm: async () => {
        setShowModal(true);
      },
      refreshQuote: () => requestQuote(),
    };
    return () => {
      delete window.__rampsDebug;
    };
  }, [IS_DEV]);

  return (
    <div className="ramps">
      <style>{`
        .ramps {
          --bg: #0f1220;
          --card: #151a2b;
          --muted: #9aa4b2;
          --text: #e9edf5;
          --accent: #39FF14;
          --accent-weak: rgba(57,255,20,0.18);
          --border: #21283d;
          --shadow: 0 10px 30px rgba(0,0,0,0.35);
          color: var(--text);
          padding: 24px;
        }
        .ramps__header { margin-bottom: 18px; }
        .ramps__title { margin: 0 0 4px; font-size: 24px; font-weight: 700; }
        .ramps__subtitle { margin: 0; color: var(--muted); }
        .ramps__grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-bottom: 16px;
        }
        @media (max-width: 900px) {
          .ramps__grid { grid-template-columns: 1fr; }
        }
        .card {
          background: var(--card);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 18px;
          box-shadow: var(--shadow);
        }
        .card__title {
          font-size: 14px;
          color: var(--muted);
          margin-bottom: 10px;
          text-transform: uppercase;
          letter-spacing: .3px;
        }
        .metric { display: flex; align-items: baseline; gap: 8px; }
        .metric__value { font-size: 28px; font-weight: 800; }
        .metric__suffix { color: var(--muted); }
        .address__row {
          display: grid;
          grid-template-columns: 120px 1fr;
          gap: 8px;
          margin-bottom: 6px;
        }
        .address__label { color: var(--muted); }
        .address__value--mono { font-family: monospace; }
        .form { display: grid; gap: 16px; }
        .form__label { color: var(--muted); font-size: 13px; }

        .divider {
          display: flex; align-items: center; gap: 10px;
          color: var(--muted);
        }
        .divider::before, .divider::after {
          content: ""; height: 1px; background: var(--border); flex: 1;
        }

        .table { display: grid; gap: 12px; }
        .table__head, .table__row {
          display: grid;
          /* Más espacio para campos de placeholder largos */
          grid-template-columns: 1.5fr 0.6fr 1.3fr 1fr 0.9fr 1.3fr 112px;
          gap: 12px;
          align-items: start;
        }
        .table__head {
          color: var(--muted);
          font-size: 12px;
          text-transform: uppercase;
        }
        @media (max-width: 1024px) {
          .table__head, .table__row { grid-template-columns: 1fr; }
        }

        /* Contenedor de cada campo para poder mostrar hint debajo */
        .field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }

        .input {
          background: #0e1322;
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 10px 12px;
          color: var(--text);
          width: 100%;
          min-width: 0; /* evita overflow en grid */
        }
        .input::placeholder {
          opacity: 0.9;
        }
        .input:focus {
          border-color: var(--accent);
          box-shadow: 0 0 0 3px var(--accent-weak);
          outline: none;
        }
        .hint {
          font-size: 12px;
          line-height: 1.2;
          color: var(--muted);
          white-space: normal; /* el hint sí hace wrap */
        }

        .btn {
          background: #1b2236;
          border: 1px solid var(--border);
          color: var(--text);
          border-radius: 10px;
          padding: 10px 14px;
          cursor: pointer;
        }
        .btn--primary {
          background: var(--accent);
          color: #0a0a0a;
          font-weight: 700;
          border-color: transparent;
        }
        .btn--ghost {
          background: transparent;
          border: 1px solid var(--border);
          color: var(--muted);
        }
        .form__actions { display: flex; align-items: center; gap: 12px; }
        .spacer { flex: 1; }

        .quoteInline {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 13px;
          color: var(--muted);
          margin-top: 4px;
          margin-bottom: 10px;
        }
        .quoteLabel { font-weight: 600; }
        .quoteValue { color: #8ef7b5; font-weight: 700; }
        .quoteValue.muted { color: var(--muted); font-weight: 500; }
        .quoteValue.error { color: #ff7675; font-weight: 600; }
        .quoteRefresh {
          background: transparent;
          border: none;
          color: var(--muted);
          cursor: pointer;
          font-size: 12px;
        }
        .quoteRefresh:hover { text-decoration: underline; }
      `}</style>

      <div className="ramps__header">
        <h1 className="ramps__title">Ramps / Remessa</h1>
        <p className="ramps__subtitle">
          Envie valores por Pix a partir do seu saldo em cripto.
        </p>
      </div>

      {!localStorage.getItem("emailUser") &&
        !localStorage.getItem("user_email") &&
        !localStorage.getItem("email") && (
          <div
            className="card"
            style={{ marginBottom: 12, borderColor: "#ff7675" }}
          >
            <b>Falta e-mail do usuário.</b> Faça login para carregar saldo e
            endereço de depósito.
          </div>
        )}

      <div className="ramps__grid">
        <section className="card">
          <div className="card__title">Saldo disponível</div>
          <div className="metric">
            {balance?.usdtFree !== undefined ? (
              <>
                <span className="metric__value">
                  {Number(balance.usdtFree).toFixed(6)}
                </span>
                <span className="metric__suffix">USDT</span>
              </>
            ) : (
              <span>Carregando…</span>
            )}
          </div>

          <div className="quoteInline">
            <span className="quoteLabel">Cotação atual (USDT → BRL):</span>
            {quoteLoading ? (
              <span className="quoteValue muted">Carregando…</span>
            ) : quoteError ? (
              <span className="quoteValue error">Falha ao obter</span>
            ) : (
              <span className="quoteValue">
                {quote?.unitPrice ? Number(quote.unitPrice).toFixed(4) : "--"}
              </span>
            )}
            <button
              className="quoteRefresh"
              type="button"
              onClick={requestQuote}
              disabled={quoteLoading}
              title="Atualizar cotação"
            >
              Atualizar
            </button>
          </div>
        </section>

        <section className="card">
          <div className="card__title">Endereço de depósito</div>
          {depositAddress?.address ? (
            <>
              <div className="address__row">
                <span className="address__label">Moeda</span>
                <span>{depositAddress.coin}</span>
              </div>
              <div className="address__row">
                <span className="address__label">Rede</span>
                <span>BEP-20 (BSC)</span>
              </div>
              <div className="address__row">
                <span className="address__label">Address</span>
                <span className="address__value--mono">
                  {depositAddress.address}
                </span>
              </div>
            </>
          ) : (
            <span>Carregando…</span>
          )}
        </section>
      </div>

      <section className="card">
        <div className="card__title">Envio de Remessa</div>

        <div style={{ marginTop: 4, color: "#9aa4b2" }}>
          Total digitado (BRL):{" "}
          <b style={{ color: "#e9edf5" }}>{Number(totalBrl).toFixed(2)}</b>
        </div>

        <form onSubmit={handleSubmit} className="form" style={{ marginTop: 12 }}>
          <div>
            <label className="form__label">Importar CSV</label>
            <input type="file" accept=".csv" onChange={handleCsvUpload} />
            {fileName && (
              <span style={{ color: "#9aa4b2", marginLeft: 8 }}>{fileName}</span>
            )}
          </div>

          <div className="divider">
            <span>ou</span>
          </div>

          <div className="table">
            <div className="table__head">
              <div>Chave Pix</div>
              <div>Tipo</div>
              <div>Nome</div>
              <div>Documento</div>
              <div>Valor (BRL)</div>
              <div>Mensagem (opcional)</div>
              <div></div>
            </div>

            {formData.map((row, i) => (
              <div className="table__row" key={i}>
                <div className="field">
                  <input
                    className="input"
                    type="text"
                    placeholder="Chave Pix"
                    title="Chave Pix (CPF, e-mail, telefone ou EVP)"
                    value={row.pixKey}
                    onChange={(e) => handleChange(i, "pixKey", e.target.value)}
                    required
                  />
                  <div className="hint">CPF, e-mail, telefone ou EVP.</div>
                </div>

                <div className="field">
                  <select
                    className="input"
                    value={row.pixKeyType}
                    onChange={(e) =>
                      handleChange(i, "pixKeyType", e.target.value)
                    }
                    title="Tipo da chave Pix (CPF/CNPJ/EMAIL/PHONE/EVP)"
                  >
                    <option value="CPF">CPF</option>
                    <option value="CNPJ">CNPJ</option>
                    <option value="EMAIL">EMAIL</option>
                    <option value="PHONE">PHONE</option>
                    <option value="EVP">EVP</option>
                  </select>
                  <div className="hint">Selecione o tipo da chave.</div>
                </div>

                <div className="field">
                  <input
                    className="input"
                    type="text"
                    placeholder="Nome completo"
                    title="Nome completo / Razão social do destinatário"
                    value={row.name}
                    onChange={(e) => handleChange(i, "name", e.target.value)}
                    required
                  />
                  <div className="hint">Nome do titular da chave.</div>
                </div>

                <div className="field">
                  <input
                    className="input"
                    type="text"
                    placeholder="Documento"
                    title="Documento (CPF ou CNPJ) — apenas números"
                    value={row.doc}
                    onChange={(e) => handleChange(i, "doc", e.target.value)}
                    required
                  />
                  <div className="hint">CPF/CNPJ (somente números).</div>
                </div>

                <div className="field">
                  <input
                    className="input"
                    type="number"
                    placeholder="Valor"
                    title="Valor em BRL (use ponto como separador decimal)"
                    value={row.amountBRL}
                    onChange={(e) =>
                      handleChange(
                        i,
                        "amountBRL",
                        e.target.value.replace(",", ".")
                      )
                    }
                    required
                    min="0"
                    step="0.01"
                  />
                  <div className="hint">Valor em reais (BRL).</div>
                </div>

                <div className="field">
                  <input
                    className="input"
                    type="text"
                    placeholder="Mensagem"
                    title="Mensagem opcional para o destinatário (até 140 caracteres, se aplicável)"
                    value={row.message}
                    onChange={(e) => handleChange(i, "message", e.target.value)}
                  />
                  <div className="hint">Opcional.</div>
                </div>

                <button
                  className="btn btn--ghost"
                  type="button"
                  onClick={() => removeRow(i)}
                  title="Remover linha"
                >
                  Remover
                </button>
              </div>
            ))}
          </div>

          <div className="form__actions">
            <button type="button" className="btn" onClick={addRow}>
              + Adicionar linha
            </button>
            <div className="spacer" />
            <button
              type="submit"
              className="btn btn--primary"
              disabled={submitDisabled}
              title={!IS_DEV && walletUsdt <= 0 ? "Saldo insuficiente" : undefined}
            >
              {isSubmitting ? "Enviando..." : "Validar e Enviar"}
            </button>
          </div>
        </form>
      </section>

      <RampsPrecheckModal
        open={showPrecheck}
        rows={formData}
        onCancel={() => setShowPrecheck(false)}
        onContinue={async () => {
          setShowPrecheck(false);
          setShowModal(true);
        }}
      />

      <RampsConfirmModal
        open={showModal}
        onCancel={() => setShowModal(false)}
        onConfirm={confirmSubmit}
        rows={formData}
        quote={quote}
        quoteLoading={quoteLoading}
        quoteError={quoteError}
        walletUsdt={walletUsdt}
        sending={isSubmitting}
      />
    </div>
  );
}



















